import { useRef, useState } from "react";
import { useMobileViewportBlankTap } from "./useMobileViewportBlankTap";
import { useSceneContextMenuActions } from "./useSceneContextMenuActions";
import { useSceneContextMenuState } from "./useSceneContextMenuState";
import { useScenePicker } from "./useScenePicker";
import { useSceneRootTargets } from "./useSceneRootTargets";
import { useSceneSelectionTargets } from "./useSceneSelectionTargets";

export function useSceneInteractionController({
  allEqualities,
  allSites,
  allTendons,
  bodies,
  bodyVisibilityUi,
  cameraRef,
  constraintHoveredKeyRef,
  constraintMarkerMeshesRef,
  constraintMarkersGroupRef,
  constraintVisibilityUi,
  controlsRef,
  effectiveWorldBodies,
  focusBodyUuid,
  focusCollisionGeomUuid,
  focusMeshUuid,
  focusModeActive,
  getJointIdFromObject,
  inertialVisibilityUi,
  isOrbitingRef,
  isPhysicsRunning,
  jointVisibilityUi,
  positionJointSliderNextToJoint,
  preferPointerAfterOrbitRef,
  rendererRef,
  resolveLibraryDropPointFromClientPoint,
  safeBodies,
  safeJointMapObject,
  sceneActions,
  sceneObjectsRef,
  sceneRef,
  selectedBodiesMultiKey,
  selectedBody,
  selectedEquality,
  selectedGeom,
  selectedInertial,
  selectedJoint,
  selectedMesh,
  selectedSite,
  selectedTendon,
  selectedVisualMeshMultiKey,
  selectionActions,
  setJointPositionSliderScreenPos,
  showCollision,
  showConstraintMarkers,
  showInertials,
  showJointPositionSlider,
  showJoints,
  showSavedCameraMarkers,
  showScaleGizmo,
  showSites,
  showVisual,
  siteVisibilityUi,
  worldBodies,
}) {
  const [hoverPreviewBody, setHoverPreviewBody] = useState(null);
  const [hoverPreviewJoint, setHoverPreviewJoint] = useState(null);
  const [hoverPreviewMesh, setHoverPreviewMesh] = useState(null);
  const sceneRmbContextAnchorRef = useRef(null);
  const sceneRmbContextDraggedRef = useRef(false);

  const contextMenuState = useSceneContextMenuState();
  const {
    activeSubmenu,
    closeSubmenuSoon,
    openSubmenu,
    sceneAddGeomKindSubmenuOpen,
    sceneAddJointKindSubmenuOpen,
    sceneAddVisualGeomKindSubmenuOpen,
    sceneContextMenuData,
    sceneContextMenuOpen,
    sceneContextMenuPos,
    sceneContextMenuRef,
    setActiveSubmenu,
    setSceneAddGeomKindSubmenuOpen,
    setSceneAddJointKindSubmenuOpen,
    setSceneAddVisualGeomKindSubmenuOpen,
    setSceneContextMenuData,
    setSceneContextMenuOpen,
    setSceneContextMenuPos,
  } = contextMenuState;

  const { bodyRootMap, worldRootTargetBodyUuid } = useSceneRootTargets({
    bodies,
    effectiveWorldBodies,
    safeBodies,
    worldBodies,
  });

  const {
    applyInspectCandidateSelection,
    applyVisualMeshMultiSelection,
    collectInspectCandidatesFromIntersects,
    findValidTarget,
    getCollisionTarget,
    getContextTargetSnapshot,
    getInertialTarget,
    getOwningBodyUuidForObject,
    getResolvedFocusBodiesGateSet,
    getSiteTarget,
    getVisualGeomTarget,
    handleContextInspectSelect,
    handleSceneContextMenuCapture,
    hasContextTarget,
    isCollisionGeomObject,
  } = useSceneSelectionTargets({
    bodyRootMap,
    cameraRef,
    constraintMarkerMeshesRef,
    constraintMarkersGroupRef,
    getJointIdFromObject,
    isPhysicsRunning,
    rendererRef,
    sceneContextMenuData,
    sceneObjectsRef,
    sceneRef,
    sceneRmbContextAnchorRef,
    sceneRmbContextDraggedRef,
    selectionActions,
    setActiveSubmenu,
    setSceneAddGeomKindSubmenuOpen,
    setSceneAddJointKindSubmenuOpen,
    setSceneAddVisualGeomKindSubmenuOpen,
    setSceneContextMenuData,
    setSceneContextMenuOpen,
    setSceneContextMenuPos,
    showCollision,
    showConstraintMarkers,
    showInertials,
    showJoints,
    showSavedCameraMarkers,
    showSites,
    showVisual,
  });

  const {
    contextAddTargetBodyUuid,
    handleContextAddBody,
    handleContextAddCameraFromScenePick,
    handleContextAddConstraint,
    handleContextAddFixedWorldCamera,
    handleContextAddGeomToBody,
    handleContextAddJointToBody,
    handleContextAddSiteToBody,
    handleContextAddTendon,
    handleContextAddVisualGeomToBody,
    handleContextClearScene,
    handleContextDeleteSelect,
    handleContextDuplicateBody,
    handleContextDuplicateCollision,
  } = useSceneContextMenuActions({
    allEqualities,
    allSites,
    allTendons,
    isPhysicsRunning,
    resolveLibraryDropPointFromClientPoint,
    safeBodies,
    safeJointMapObject,
    sceneActions,
    sceneContextMenuData,
    sceneObjectsRef,
    sceneRef,
    selectionActions,
    setActiveSubmenu,
    setSceneAddGeomKindSubmenuOpen,
    setSceneAddJointKindSubmenuOpen,
    setSceneAddVisualGeomKindSubmenuOpen,
    setSceneContextMenuOpen,
    worldRootTargetBodyUuid,
  });

  useScenePicker({
    rendererRef,
    sceneRef,
    cameraRef,
    controlsRef,
    constraintMarkersGroupRef,
    constraintMarkerMeshesRef,
    constraintHoveredKeyRef,
    sceneRmbContextAnchorRef,
    sceneRmbContextDraggedRef,
    sceneContextMenuOpen,
    sceneContextMenuRef,
    setSceneContextMenuOpen,
    setSceneContextMenuPos,
    setSceneContextMenuData,
    setActiveSubmenu,
    setHoverPreviewBody,
    setHoverPreviewJoint,
    setHoverPreviewMesh,
    isOrbitingRef,
    preferPointerAfterOrbitRef,
    showVisual,
    showJoints,
    showCollision,
    showInertials,
    showSites,
    showConstraintMarkers,
    jointVisibilityUi,
    siteVisibilityUi,
    inertialVisibilityUi,
    bodyVisibilityUi,
    constraintVisibilityUi,
    selectedBody,
    selectedMesh,
    selectedJoint,
    selectedSite,
    selectedGeom,
    selectedInertial,
    selectedEquality,
    selectedTendon,
    focusModeActive,
    focusBodyUuid,
    focusMeshUuid,
    focusCollisionGeomUuid,
    selectionActions,
    getCollisionTarget,
    getVisualGeomTarget,
    getInertialTarget,
    getSiteTarget,
    findValidTarget,
    getResolvedFocusBodiesGateSet,
    collectInspectCandidatesFromIntersects,
    applyVisualMeshMultiSelection,
    applyInspectCandidateSelection,
    getContextTargetSnapshot,
    handleSceneContextMenuCapture,
    positionJointSliderNextToJoint,
    showJointPositionSlider,
    showSavedCameraMarkers,
    showScaleGizmo,
    setJointPositionSliderScreenPos,
    isCollisionGeomObject,
    getOwningBodyUuidForObject,
    getJointIdFromObject,
    selectedVisualMeshMultiKey,
    selectedBodiesMultiKey,
  });

  const mobileViewportBlankTapHandlers = useMobileViewportBlankTap({
    setActiveSubmenu,
    setSceneAddGeomKindSubmenuOpen,
    setSceneAddJointKindSubmenuOpen,
    setSceneAddVisualGeomKindSubmenuOpen,
    setSceneContextMenuOpen,
  });

  return {
    contextMenuProps: {
      activeSubmenu,
      closeSubmenuSoon,
      contextAddTargetBodyUuid,
      handleContextClearScene,
      handleContextDeleteSelect,
      handleContextDuplicateBody,
      handleContextDuplicateCollision,
      handleContextAddBody,
      handleContextAddCameraFromScenePick,
      handleContextAddConstraint,
      handleContextAddGeomToBody,
      handleContextAddJointToBody,
      handleContextAddSiteToBody,
      handleContextAddTendon,
      handleContextAddVisualGeomToBody,
      handleContextAddFixedWorldCamera,
      handleContextInspectSelect,
      hasContextTarget,
      isPhysicsRunning,
      openSubmenu,
      safeBodies,
      safeJointMapObject,
      sceneAddGeomKindSubmenuOpen,
      sceneAddJointKindSubmenuOpen,
      sceneAddVisualGeomKindSubmenuOpen,
      sceneContextMenuData,
      sceneContextMenuOpen,
      sceneContextMenuPos,
      sceneContextMenuRef,
      setActiveSubmenu,
      setSceneAddGeomKindSubmenuOpen,
      setSceneAddJointKindSubmenuOpen,
      setSceneAddVisualGeomKindSubmenuOpen,
      worldRootTargetBodyUuid,
    },
    getOwningBodyUuidForObject,
    handleSceneContextMenuCapture,
    hoverPreviewBody,
    hoverPreviewJoint,
    hoverPreviewMesh,
    mobileViewportBlankTapHandlers,
    setHoverPreviewBody,
    setHoverPreviewJoint,
    setHoverPreviewMesh,
  };
}
